/**
 * GraphQL Queries for Workflow Template Metadata
 *
 * Backend integration for Metadata DSL v2.0.
 * Queries the GraphQL API implemented in Phase 2.
 *
 * FERP Principles:
 * - Data-driven: All templates from DynamoDB via GraphQL
 * - Domain-agnostic: Queries work for any domain
 * - Life-critical: Complete metadata with audit trails
 * - Single code path: Frontend → GraphQL → Repository → DynamoDB
 */

import { gql } from '@apollo/client';

/**
 * List templates by domain
 *
 * Access Pattern: GSI1 query (sorted by usage count)
 * Used for: Template selection palette
 */
export const LIST_TEMPLATES_BY_DOMAIN = gql`
  query ListTemplatesByDomain($domain: String!, $templateType: String, $limit: Int) {
    listTemplatesByDomain(domain: $domain, templateType: $templateType, limit: $limit) {
      templateId
      templateVersion
      domain
      templateType
      name
      category
      description
      icon
      tags
      usageCount
      createdAt
      updatedAt
    }
  }
`;

/**
 * List templates by tenant
 *
 * Access Pattern: GSI2 query
 * Used for: Tenant-specific template management
 */
export const LIST_TEMPLATES_BY_TENANT = gql`
  query ListTemplatesByTenant($tenant: String!, $limit: Int) {
    listTemplatesByTenant(tenant: $tenant, limit: $limit) {
      templateId
      templateVersion
      domain
      templateType
      name
      category
      description
      icon
      tags
      usageCount
      createdAt
      updatedAt
    }
  }
`;

/**
 * Get complete template metadata by ID and version
 *
 * Access Pattern: Direct PK/SK lookup
 * Used for: Loading template for interpreter
 */
export const GET_TEMPLATE_METADATA = gql`
  query GetTemplateMetadata($tenant: String!, $templateId: String!, $version: String!) {
    getTemplateMetadata(tenant: $tenant, templateId: $templateId, version: $version) {
      templateId
      templateVersion
      schemaVersion
      tenant
      domain
      templateType

      metadata {
        business {
          name
          domain
          category
          description
          icon
          tags
        }

        ui {
          layout
          steps {
            id
            label
            description
            fields {
              id
              component
              label
              required
              placeholder
              helpText
              dataSource
              validation
              mapsTo {
                primitiveType
                primitiveId
                fieldPath
                transformation
                auditTracking
              }
            }
          }
        }

        primitives {
          nodes {
            id
            type
            name
            position {
              x
              y
            }
            metadata
          }
          connections {
            id
            source
            target
            connectionType
          }
        }

        mappings {
          uiToPrimitive
          primitiveToUi
          businessToTechnical
          technicalToBusiness
          eventLabels
        }

        compliance {
          frameworks
          auditLevel
        }
      }

      createdAt
      createdBy
      updatedAt
      updatedBy

      versionHistory {
        version
        updatedAt
        changes
      }

      usageCount
      lastUsedAt
    }
  }
`;

/**
 * Get latest version of template
 *
 * Access Pattern: Query by PK, sort SK descending
 * Used for: Loading most recent template version
 */
export const GET_LATEST_TEMPLATE_METADATA = gql`
  query GetLatestTemplateMetadata($tenant: String!, $templateId: String!) {
    getLatestTemplateMetadata(tenant: $tenant, templateId: $templateId) {
      templateId
      templateVersion
      schemaVersion
      tenant
      domain
      templateType

      metadata {
        business {
          name
          domain
          category
          description
          icon
          tags
        }

        ui {
          layout
          steps {
            id
            label
            description
            fields {
              id
              component
              label
              required
              placeholder
              helpText
              dataSource
              validation
              mapsTo {
                primitiveType
                primitiveId
                fieldPath
                transformation
                auditTracking
              }
            }
          }
        }

        primitives {
          nodes {
            id
            type
            name
            position {
              x
              y
            }
            metadata
          }
          connections {
            id
            source
            target
            connectionType
          }
        }

        mappings {
          uiToPrimitive
          primitiveToUi
          businessToTechnical
          technicalToBusiness
          eventLabels
        }

        compliance {
          frameworks
          auditLevel
        }
      }

      createdAt
      createdBy
      updatedAt
      updatedBy

      versionHistory {
        version
        updatedAt
        changes
      }

      usageCount
      lastUsedAt
    }
  }
`;

/**
 * Validate template metadata
 *
 * Used for: Pre-flight validation before using template
 */
export const VALIDATE_TEMPLATE_METADATA = gql`
  query ValidateTemplateMetadata($metadata: TemplateMetadataContentInput!) {
    validateTemplateMetadata(metadata: $metadata) {
      valid
      errors
      warnings
    }
  }
`;

/**
 * Validate template metadata against standards packs
 *
 * Phase 11 Validation Framework: 6-layer validation
 * Used for: Real-time validation with fix suggestions
 */
export const VALIDATE_TEMPLATE_METADATA_WITH_STANDARDS = gql`
  query ValidateTemplateMetadataWithStandards($metadata: TemplateMetadataContentInput!) {
    validateTemplateMetadata(metadata: $metadata) {
      success
      totalLayers
      layersExecuted
      violations {
        severity
        code
        layer
        field
        message
        reference
        fixSuggestion
      }
      executionTrace
    }
  }
`;

/**
 * Increment template usage
 *
 * Mutation: Atomic counter update
 * Used for: Tracking template popularity
 */
export const INCREMENT_TEMPLATE_USAGE = gql`
  mutation IncrementTemplateUsage($tenant: String!, $templateId: String!, $version: String!) {
    incrementTemplateUsage(tenant: $tenant, templateId: $templateId, version: $version)
  }
`;

/**
 * Create template metadata
 *
 * Mutation: Create new template
 * Used for: Admin template creation
 */
export const CREATE_TEMPLATE_METADATA = gql`
  mutation CreateTemplateMetadata($input: CreateTemplateMetadataInput!) {
    createTemplateMetadata(input: $input) {
      template_id
      template_version
      domain
      created_at
    }
  }
`;

/**
 * Update template metadata
 *
 * Mutation: Create new version of template
 * Used for: Template versioning
 */
export const UPDATE_TEMPLATE_METADATA = gql`
  mutation UpdateTemplateMetadata($input: UpdateTemplateMetadataInput!) {
    updateTemplateMetadata(input: $input) {
      template_id
      template_version
      updated_at
    }
  }
`;

/**
 * Delete template metadata
 *
 * Mutation: Soft delete (life-critical compliance)
 * Used for: Template archiving
 */
export const DELETE_TEMPLATE_METADATA = gql`
  mutation DeleteTemplateMetadata(
    $tenant: String!
    $templateId: String!
    $version: String!
    $actor: String!
  ) {
    deleteTemplateMetadata(
      tenant: $tenant
      templateId: $templateId
      version: $version
      actor: $actor
    )
  }
`;

/**
 * TypeScript types for query variables and results
 *
 * Preserve all fields for type safety.
 */

export interface ListTemplatesByDomainVariables {
  domain: string;
  templateType?: string;
  limit?: number;
}

export interface ListTemplatesByTenantVariables {
  tenant: string;
  limit?: number;
}

export interface GetTemplateMetadataVariables {
  tenant: string;
  templateId: string;
  version: string;
}

export interface GetLatestTemplateMetadataVariables {
  tenant: string;
  templateId: string;
}

export interface IncrementTemplateUsageVariables {
  tenant: string;
  templateId: string;
  version: string;
}

export interface TemplateListItem {
  templateId: string;
  templateVersion: string;
  domain: string;
  templateType: string;
  name: string;
  category: string;
  description: string;
  icon: string | null;
  tags: string[];
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowTemplateMetadataResult {
  templateId: string;
  templateVersion: string;
  schemaVersion: string;
  tenant: string;
  domain: string;
  templateType: string;
  metadata: {
    business: {
      name: string;
      domain: string;
      category: string;
      description: string;
      icon: string | null;
      tags: string[];
    };
    ui: {
      layout: string;
      steps: Array<{
        id: string;
        label: string;
        description: string | null;
        fields: Array<{
          id: string;
          component: string;
          label: string;
          required: boolean;
          placeholder: string | null;
          helpText: string | null;
          dataSource: any;
          validation: any;
          mapsTo: Array<{
            primitiveType: string;
            primitiveId: string;
            fieldPath: string;
            transformation: any;
            auditTracking: any;
          }>;
        }>;
      }>;
    };
    primitives: {
      nodes: Array<{
        id: string;
        type: string;
        name: string;
        position: {
          x: number;
          y: number;
        } | null;
        metadata: any;
      }>;
      connections: Array<{
        id: string;
        source: string;
        target: string;
        connectionType: string | null;
      }>;
    };
    mappings: {
      uiToPrimitive: any;
      primitiveToUi: any;
      businessToTechnical: any;
      technicalToBusiness: any;
      eventLabels: any;
    };
    compliance: {
      frameworks: string[];
      auditLevel: string;
    };
  };
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  versionHistory: Array<{
    version: string;
    updatedAt: string;
    changes: string;
  }>;
  usageCount: number;
  lastUsedAt: string | null;
}

/**
 * Validation violation from Phase 11 Validation Framework
 */
export interface ValidationViolation {
  severity: 'ERROR' | 'WARNING' | 'INFO';
  code: string;
  layer: number;
  field: string | null;
  message: string;
  reference: string;
  fixSuggestion: any;
}

/**
 * Validation result from Phase 11 Validation Framework
 */
export interface ValidationResult {
  success: boolean;
  totalLayers: number;
  layersExecuted: number;
  violations: ValidationViolation[];
  executionTrace: string[];
}

export interface ValidateTemplateMetadataVariables {
  metadata: any;
}
